import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
    Breakpoints,
    Container,
    CTA,
    Text,
    TextTag,
    TokenTextAppearance,
    Select,
    TokenTextColor,
    Fieldset,
} from '@volkswagen-onehub/components-core'
import {SizeEnum} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {Global} from '../../shared/variables'
import {openExternalLinkPopUpLayer} from '../layers/launchers/ExternalLinkPupUpLayerHandle'
import {AppDispatch} from '../thunk'
import {setExternalLinkPopUp} from '../../features/layersHandle/layersHandleSlice'
import {ExternalLinkPopup} from '../../types/ExternalLinkPopup'
import {Province} from '../../types/Province'
import {Dealer} from '../../types/Dealer'
import axios from 'axios'
import {openAlertSystemLayer} from '../layers/launchers/AlertSystemLayerHandle'
import {
    eventInfoCommon,
    TrackingAction,
    trackingEvent,
} from '../../shared/trackingServiceHelper'

const FormContainer = styled.div`
    background: ${({theme}) => theme.colors.background.tertiary};
    border: 0;
    border-radius: ${({theme}) => theme.size.borderRadius.card};
    box-shadow: ${({theme}) => theme.colors.shadow.smallHover};
    margin-top: ${(props) => props.theme.size.static565};
    padding: ${(props) => props.theme.size.dynamic0270}
        ${(props) => props.theme.size.dynamic0100};
    @media (min-width: ${Breakpoints.b960}px) {
        padding: ${(props) => props.theme.size.dynamic0200};
    }
`

const SelectsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: ${(props) => props.theme.size.static350};
    margin: ${(props) => props.theme.size.static530} 0
        ${(props) => props.theme.size.static450} 0;

    @media (min-width: ${Breakpoints.b960}px) {
        grid-template-columns: 1fr 1fr;
        grid-gap: ${(props) => props.theme.size.static530};
    }
`

interface FormProps {
    simulationId: number
}

export function Form(props: FormProps): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const dispatch = useDispatch() as AppDispatch

    const serviceType = useSelector(
        (state: RootState) => state.general.serviceType
    )

    const isPrivate = serviceType === Global.PRIVATE

    const provinces = useSelector(
        (state: RootState) => state.dealerFormData.allProvinces
    )

    const dealers = useSelector(
        (state: RootState) => state.dealerFormData.dealersOptions
    )

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )

    const [dealersOptions, setDealersOptions] = useState<Dealer[]>([])
    const [selectedProvince, setSelectedProvince] = useState('')
    const [selectedDealerCode, setSelectedDealerCode] = useState('')
    const [offerUrl, setOfferUrl] = useState('')

    useEffect(() => {
        if (selectedProvince) {
            const filteredDealers = dealers.filter(
                (dealer: Dealer) =>
                    dealer.province === selectedProvince &&
                    dealer.service_type === serviceType
            )

            setDealersOptions(filteredDealers)
        }
    }, [selectedProvince])

    const getOfferUrl = (simulationId: number, dealerCode: string) => {
        axios
            .post(`${backendUrl}/api/service_partners/get_offer`, {
                simulationId,
                dealerCode,
            })
            .then((response) => {
                setOfferUrl(response.data.url)
            })
            .catch(() => {
                openAlertSystemLayer()
            })
    }

    const getDealerCode = (value: string) => {
        const filteredDealer: any = dealersOptions.find(
            (dealer: Dealer) => dealer.company === value
        )

        if (filteredDealer) {
            const dealerCode: string = filteredDealer.company_code
            setSelectedDealerCode(dealerCode)
        }
    }

    useEffect(() => {
        if (selectedDealerCode) {
            getOfferUrl(props.simulationId, selectedDealerCode)
        }
    }, [selectedDealerCode])

    const onSubmit = () => {
        if (offerUrl) {
            const externalLinkPopup: ExternalLinkPopup = {
                title: t('dealer-form.pop-up-layer.title'),
                text: t('dealer-form.pop-up-layer.text'),
                ctaText: t('cta.external-link'),
                ctaUrl: offerUrl,
            }
            dispatch(setExternalLinkPopUp(externalLinkPopup))
            openExternalLinkPopUpLayer()
        }
    }

    return (
        <FormContainer>
            <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline300}>
                <span
                    dangerouslySetInnerHTML={{
                        __html: isPrivate
                            ? t('dealer-form.form.title-private')
                            : t('dealer-form.form.title-business'),
                    }}
                />
            </Text>

            <form>
                <Fieldset>
                    <SelectsContainer>
                        <Container gutter={SizeEnum.static100} wrap="always">
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.bigcopy100}
                                bold
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'dealer-form.form.labels.province'
                                        ),
                                    }}
                                />
                            </Text>

                            <Select
                                label={t(
                                    'dealer-form.form.placeholders.province'
                                )}
                                isFloating
                                defaultValue=""
                                onChange={(value) => {
                                    setSelectedProvince(
                                        value.currentTarget.value
                                    )
                                }}
                            >
                                <option value="">
                                    {t(
                                        'dealer-form.form.placeholders.province'
                                    )}
                                </option>
                                {provinces.map(
                                    (province: Province, index: number) => (
                                        <option
                                            key={index}
                                            value={province.province}
                                        >
                                            {province.province}
                                        </option>
                                    )
                                )}
                            </Select>
                        </Container>
                        <Container gutter={SizeEnum.static100} wrap="always">
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.bigcopy100}
                                bold
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'dealer-form.form.labels.dealer'
                                        ),
                                    }}
                                />
                            </Text>

                            <Select
                                label={t(
                                    'dealer-form.form.placeholders.dealer'
                                )}
                                name="dealer"
                                isFloating
                                disabled={selectedProvince.length === 0}
                                onChange={(value) => {
                                    getDealerCode(value.currentTarget.value)
                                }}
                            >
                                <option value="">
                                    {t('dealer-form.form.placeholders.dealer')}
                                </option>
                                {dealersOptions.map(
                                    (dealer: Dealer, index: number) => (
                                        <option
                                            key={index}
                                            value={dealer.company}
                                        >
                                            {`${dealer.company}${Global.COMMA} ${dealer.street} ${Global.STRING_SEPARATOR} ${dealer.city}`}
                                        </option>
                                    )
                                )}
                            </Select>
                        </Container>
                    </SelectsContainer>

                    <CTA
                        tag="button"
                        emphasis="primary"
                        disabled={selectedDealerCode.length === 0}
                        onClick={(e) => {
                            e.preventDefault()
                            onSubmit()
                            trackingEvent(
                                TrackingAction.VW_BASIC_VEHICLE_BUTTON_CLICK,
                                {
                                    eventInfo: {
                                        ...eventInfoCommon,
                                        contentId: t('cta.submit'),
                                    },
                                    link: {
                                        name: t('cta.submit'),
                                        newTab: false,
                                        url: window.location.href,
                                    },
                                }
                            )
                        }}
                        aria-label={t('cta.submit')}
                    >
                        {t('cta.submit')}
                    </CTA>

                    <Container
                        padding={{
                            top: {
                                [Breakpoints.default]: SizeEnum.static350,
                            },
                        }}
                        wrap="always"
                    >
                        <Text
                            tag={TextTag.p}
                            color={TokenTextColor.secondary}
                            appearance={TokenTextAppearance.copy100}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('dealer-form.form.required'),
                                }}
                            />
                        </Text>
                    </Container>
                </Fieldset>
            </form>
        </FormContainer>
    )
}
